import { useEffect } from 'react';
import { useSettings } from '@/hooks/useProducts';
import { refreshWidgets } from '@/lib/integrations/storefront-events';

interface StampedPublic {
  public_key: string;
  store_hash: string;
  hide_native: boolean;
  star_rating: boolean;
}

/** Stamped's public storefront config, or null when the connector is not connected. */
export function useStampedPublic(): StampedPublic | null {
  const { data } = useSettings();
  const cfg = data?.settings?.integrations_public?.stamped as Record<string, string> | undefined;
  if (!cfg?.public_key || !cfg?.store_hash) return null;
  return { public_key: cfg.public_key, store_hash: cfg.store_hash, hide_native: cfg.hide_native === '1', star_rating: cfg.star_rating === '1' };
}

interface ProductLike {
  id: string;
  name: string;
  slug?: string | null;
  sku?: string | null;
  short_description?: string | null;
  image_url?: string | null;
  images?: Array<{ url: string }>;
}

/** Main reviews widget for a product page. Renders nothing unless Stamped is connected. */
export default function StampedReviewsWidget({ product }: { product: ProductLike }) {
  const stamped = useStampedPublic();
  useEffect(() => {
    if (stamped) refreshWidgets();
  }, [stamped?.store_hash, product.id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!stamped) return null;
  const image = product.images?.[0]?.url ?? product.image_url ?? '';
  const url = `${window.location.origin}/product/${product.slug ?? product.id}`;
  return (
    <div
      id="stamped-main-widget"
      data-testid="stamped-main-widget"
      data-product-id={product.id}
      data-name={product.name}
      data-url={url}
      data-image-url={image}
      data-description={product.short_description ?? ''}
      data-product-sku={product.sku ?? product.slug ?? ''}
    />
  );
}

/** Star badge for product pages/cards. */
export function StampedStarRating({ productId, className }: { productId: string; className?: string }) {
  const stamped = useStampedPublic();
  useEffect(() => {
    if (stamped) refreshWidgets();
  }, [stamped?.store_hash, productId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!stamped || !stamped.star_rating) return null;
  return <span className={`stamped-product-reviews-badge stamped-main-badge ${className ?? ''}`.trim()} data-testid="stamped-badge" data-id={productId} />;
}
